import React from "react";
import { Helmet } from "react-helmet-async";
import { useSiteSettings } from "../../lib/settings";
import { useI18n } from "../../lib/i18n";
import { resolveImage } from "../admin/ImageUpload";

/* -------------------------------------------------------------------------
 * Global schema.org JSON-LD injector.
 *
 * Adds three critical structured-data blocks Google uses to render rich SERP
 * results (title + description + sitelinks + logo):
 *   1) Organization  — brand name, logo, description, contact, sameAs socials
 *   2) WebSite       — includes Sitelinks Search Box + priority pages
 *   3) BreadcrumbList (per priority page) — helps Google build sitelinks list
 *
 * All content is read from SiteSettings so admins can update copy without
 * touching code. Runs on every page — safe to include multiple times as Helmet
 * dedupes.
 * ------------------------------------------------------------------------ */

const OrgSchema = () => {
  const settings = useSiteSettings();
  const { lang } = useI18n();

  const base = (settings.public_base_url || "https://firatconstruction.com").replace(/\/$/, "");
  const orgName = settings.company_name || "Fırat Construction Yapı A.Ş.";
  const desc = (settings.seo_org_description && (settings.seo_org_description[lang] || settings.seo_org_description.tr)) || "";
  const logo = settings.logo_url ? resolveImage(settings.logo_url) : "";
  const socials = (settings.seo_social_links || []).filter(Boolean);
  const priority = settings.seo_priority_pages || [];
  const sitelinkSearch = settings.seo_sitelink_search !== false;

  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: orgName,
    url: base,
    ...(logo ? { logo } : {}),
    ...(desc ? { description: desc } : {}),
    ...(settings.seo_founding_year ? { foundingDate: String(settings.seo_founding_year) } : {}),
    ...(settings.seo_founder_name ? { founder: { "@type": "Person", name: settings.seo_founder_name } } : {}),
    ...((settings.seo_contact_phone || settings.seo_contact_email) ? {
      contactPoint: [{
        "@type": "ContactPoint",
        contactType: "customer service",
        ...(settings.seo_contact_phone ? { telephone: settings.seo_contact_phone } : {}),
        ...(settings.seo_contact_email ? { email: settings.seo_contact_email } : {}),
        areaServed: "TR",
        availableLanguage: ["Turkish", "English", "Arabic"],
      }],
    } : {}),
    ...(settings.seo_address_street || settings.seo_address_city ? {
      address: {
        "@type": "PostalAddress",
        ...(settings.seo_address_street ? { streetAddress: settings.seo_address_street } : {}),
        ...(settings.seo_address_city ? { addressLocality: settings.seo_address_city } : {}),
        ...(settings.seo_address_region ? { addressRegion: settings.seo_address_region } : {}),
        ...(settings.seo_address_postal ? { postalCode: settings.seo_address_postal } : {}),
        ...(settings.seo_address_country ? { addressCountry: settings.seo_address_country } : {}),
      },
    } : {}),
    ...(socials.length ? { sameAs: socials } : {}),
  };

  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: orgName,
    url: base,
    ...(sitelinkSearch ? {
      potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: `${base}/search?q={search_term_string}` },
        "query-input": "required name=search_term_string",
      },
    } : {}),
  };

  // ItemList schema — presents "priority pages" as candidate sitelinks
  const itemList = priority.length ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: priority.map((p, i) => ({
      "@type": "SiteNavigationElement",
      position: i + 1,
      name: p.title,
      description: p.description,
      url: `${base}${p.path || "/"}`,
    })),
  } : null;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(organization)}</script>
      <script type="application/ld+json">{JSON.stringify(website)}</script>
      {itemList ? (
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
      ) : null}
    </Helmet>
  );
};

export default OrgSchema;
